// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { createTheme } from '@mui/material';
import { AppIcon } from '@shared/ui-kit/components';
import { hexToRgba } from '@shared/ui-kit/utils/hex-to-rgba';
import { buttons } from './buttons';
import { baseTheme } from './base';
import { typography } from './typography';
import { slider } from './slider';

const components = createTheme({
  components: {
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          fontSize: '1em',
          color: '#79828D',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          fontSize: '1em',
          height: '32px',
          borderRadius: '8px',
          backgroundColor: '#FFFFFF',
          transition: 'border-color 200ms, background-color 200ms, color 200ms',
          fieldset: {
            borderColor: '#CACED8',
          },
          '&.options-selector-empty, &.options-selector-empty-required': {
            backgroundColor: '#FFFFFF',
            color: '#9FADBE !important',
          },
          '&.options-selector-filled': {
            backgroundColor: '#FFFFFF',
            color: '#1A2140 !important',
          },
          '&.options-selector-filled.options-selector-gray': {
            backgroundColor: '#EDF0F9',
            color: '#1A2140 !important',
          },
          '&:hover:not(.Mui-disabled):not(.Mui-error) fieldset': {
            borderColor: '#515F70',
          },
          '&.Mui-focused:not(.Mui-error) fieldset': {
            borderColor: '#1072BA !important',
          },
          '&.Mui-error': {
            color: '#1A2140 !important',
            backgroundColor: '#FFFFFF',
            fieldset: {
              border: '1px solid #D80027 !important',
            },
          },
        },
        select: {
          '&:focus': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '&:before': {
            borderBottom: '2px solid green',
          },
          '&:hover:not($disabled):not($focused):not($error):before': {
            borderBottom: '2px solid blue',
          },
          '&:after': {
            borderBottom: '3px solid purple',
          },
        },
      },
    },
    MuiAccordionSummary: {
      defaultProps: {
        expandIcon: <AppIcon icon="ArrowDown" color="disabled" />,
      },
    },
    MuiAccordion: {
      defaultProps: {
        square: true,
      },
      styleOverrides: {
        root: {
          marginBottom: '10px',
          boxShadow: 'none',
          borderRadius: '10px',
          backgroundColor: '#edf0f9',
          border: 'none',
          '&.MuiAccordion-root:before': {
            backgroundColor: 'transparent',
          },
          '&.Mui-disabled': {
            backgroundColor: 'white',
          },
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        checkedIcon: <AppIcon icon="Checked" size="base" />,
        icon: <AppIcon icon="Checkbox" size="base" />,
        indeterminateIcon: <AppIcon icon="Indeterminate" size="base" />,
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontFamily: 'Poppins',
          backgroundColor: '#515F70',
          fontSize: 'calc(14rem/16)',
          fontWeight: 400,
        },
        arrow: {
          color: '#515F70',
        },
      },
      defaultProps: {
        placement: 'top',
        arrow: true,
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          '&.MuiChip-colorDefault': {
            color: '#79828D',
            backgroundColor: hexToRgba('#79828D', 0.1),
            fontWeight: 600,
          },
        },
        colorSuccess: {
          color: '#0D9795',
          backgroundColor: hexToRgba('#0D9795', 0.1),
          fontWeight: 600,
        },
        colorWarning: {
          color: '#D97706',
          backgroundColor: hexToRgba('#D97706', 0.1),
          fontWeight: 600,
        },
        colorError: {
          color: '#D80027',
          backgroundColor: hexToRgba('#D80027', 0.1),
          fontWeight: 600,
        },
      },
    },

    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: {
          boxSizing: 'border-box',
          backgroundColor: '#FFFFFF',
          border: '1px solid #CACED8',
          height: '30px',
          fontFamily: 'Poppins',
          fontSize: '1em',
          color: '#79828D',
          borderRadius: '8px',
          cursor: 'pointer',
          paddingRight: '10px !important',
        },
        input: {
          padding: '0px !important',
          border: 'none',
          marginInline: '1rem',
        },
        listbox: {
          backgroundColor: '#F6F7FC',
          color: '#79828D',
          fontSize: '1em',
          borderRadius: '8px',
          '::-webkit-scrollbar': {
            boxSizing: 'border-box',
            width: '3px',
            border: '1px solid red',
          },
          '::-webkit-scrollbar-track': {
            borderRadius: '5px',
            background: '#f6f7fc',
          },
          '::-webkit-scrollbar-thumb': {
            backgroundColor: '#9fadbe',
            borderRadius: '5px',
          },
          '::-webkit-scrollbar-thumb:hover': {
            background: '#9fadbe90',
          },
        },
        paper: {
          boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.05) !important',
        },
        noOptions: {
          color: '#79828D',
          fontSize: '1rem',
        },
        groupLabel: {
          color: '#515F70',
          fontWeight: 700,
          lineHeight: '24px',
          fontSize: '1em',
          fontFamily: 'Poppins',
          backgroundColor: '#F6F7FC',
        },
        groupUl: {
          padding: '0px',
          margin: '0px',
          paddingLeft: '0px !important',
        },
        option: {
          paddingLeft: '15px !important',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '8px',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: '8px',
          boxShadow: 'none',
          border: '1px solid #CACED8',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '1em',
          fontFamily: 'Poppins',
          color: '#79828D',
          '&:hover': {
            color: '#79828d',
            backgroundColor: '#F6F7FC',
          },
        },
      },
    },
    MuiSkeleton: {
      variants: [
        {
          props: { variant: 'rectangular' },
          style: { borderRadius: '8px', background: '#edf0f9' },
        },
      ],
    },
  },
});

export const theme = createTheme(
  components,
  buttons,
  slider,
  typography,
  baseTheme,
);
